import React from 'react';

import classnames from 'classnames';
import { connect } from 'react-redux';

// Utils
import { Text, Box } from '@nerdwallet/currency';
import { formatCurrency } from '@nerdwallet/nw-client-lib/num';

// Config
import { MONTHLY_PAYMENT_GOAL } from '../../goals';

// CSS
import style from './header.module.less';

const Header = ({
  monthlyPayment,
  refinanceGoal,
  howLongStay,
  breakevenBegin,
  totalSavings,
}): JSX.Element => {
  const strAbsMonthlyPayment = formatCurrency(Math.abs(monthlyPayment), false);
  const strAbsTotalSavings = formatCurrency(Math.abs(totalSavings), false);

  let title;
  let message;

  const numOfYears = howLongStay === 1 ? 'year' : `${howLongStay} years`;

  // howLongStay is the slider value
  if (breakevenBegin && breakevenBegin < howLongStay) {
    if (monthlyPayment > 0) {
      if (refinanceGoal === MONTHLY_PAYMENT_GOAL.value) {
        // scenario 1
        title = (
          <Text size="large" bold>
            Refinancing will reduce your monthly mortgage payment by{' '}
            <Text tone="positive">{strAbsMonthlyPayment}</Text>.
          </Text>
        );
        message = (
          <Text>
            By refinancing, you’ll <Text bold>save {strAbsTotalSavings}</Text>{' '}
            in the first {howLongStay} years by paying{' '}
            <Text bold>less interest</Text>.
          </Text>
        );
      } else {
        // scenario 2
        title = (
          <Text size="large" bold>
            By refinancing, you’ll save{' '}
            <Text tone="positive">{strAbsTotalSavings}</Text> in the first{' '}
            {howLongStay} years by paying less interest.
          </Text>
        );
        message = (
          <Text>
            Refinancing will <Text bold>reduce</Text> your monthly{' '}
            <Text bold>mortgage payment</Text> by{' '}
            <Text bold>{strAbsMonthlyPayment}</Text>.
          </Text>
        );
      }
    } else if (refinanceGoal === MONTHLY_PAYMENT_GOAL.value) {
      // scenario 3
      title = (
        <Text size="large" bold>
          You can’t save on your monthly payment by refinancing.
        </Text>
      );
      message = (
        <Text>
          But refinancing will <Text bold>save</Text> you{' '}
          <Text bold>{strAbsTotalSavings}</Text> interest in the first{' '}
          {numOfYears}.
        </Text>
      );
    } else {
      // scenario 4
      title = (
        <Text size="large" bold>
          By refinancing, you’ll save{' '}
          <Text tone="positive">{strAbsTotalSavings}</Text> in the first{' '}
          {howLongStay} years by paying less interest.
        </Text>
      );
      message = (
        <Text>
          Refinancing will <Text bold>increase</Text> your monthly{' '}
          <Text bold>mortgage payment</Text> by{' '}
          <Text bold>{strAbsMonthlyPayment}</Text>. But you’ll pay less total{' '}
          interest.
        </Text>
      );
    }
  } else if (monthlyPayment > 0) {
    if (refinanceGoal === MONTHLY_PAYMENT_GOAL.value) {
      // scenario 7
      title = (
        <Text size="large" bold>
          Refinancing will reduce your monthly mortgage payment by{' '}
          <Text tone="positive">{strAbsMonthlyPayment}</Text>.
        </Text>
      );
      message = (
        <Text>
          By refinancing, you’ll <Text bold>pay {strAbsTotalSavings} more</Text>{' '}
          in the first {numOfYears}.
        </Text>
      );
    } else {
      // scenario 8
      title = (
        <Text size="large" bold>
          By refinancing, you’ll pay{' '}
          <Text tone="critical">{strAbsTotalSavings}</Text> more in the first{' '}
          {numOfYears}.
        </Text>
      );
      message = (
        <Text>
          Refinancing will <Text bold>reduce</Text> your monthly{' '}
          <Text bold>mortgage payment</Text> by{' '}
          <Text bold>{strAbsMonthlyPayment}</Text>.
        </Text>
      );
    }
  } else if (refinanceGoal === MONTHLY_PAYMENT_GOAL.value) {
    // scenario 5
    title = (
      <Text size="large" bold>
        You can’t save on your monthly payment or your mortgage interest by
        refinancing.
      </Text>
    );
  } else {
    // scenario 6
    title = (
      <Text size="large" bold>
        You have a better deal with your current mortgage. If you refinance
        you’ll pay more interest.
      </Text>
    );
  }

  return (
    <Box
      className={classnames(
        style['rc-results-panel-header'],
        style['contextual-recos']
      )}
    >
      <Box className={style['rc-results-panel-header-title']}>{title}</Box>
      <Box className={style['rc-results-panel-header-message']}>{message}</Box>
    </Box>
  );
};

Header.displayName = 'Header';

const mapStateToProps = (state) => ({
  refinanceGoal: state?.refinanceCalculator?.inputs?.refinanceGoal,
  howLongStay: state?.refinanceCalculator?.inputs?.howLongStay,
  breakevenBegin: state?.refinanceCalculator?.outputs?.breakevenBegin,
  monthlyPayment:
    state?.refinanceCalculator?.outputs?.details?.savings?.monthlyPayment,
  totalSavings:
    state?.refinanceCalculator?.outputs?.details?.savings?.totalSavings,
});

export default connect(mapStateToProps)(Header);
